import type { FileRecordActionHandlerDeps } from "@/object-actions/handlers/fileRecord";
import type { ObjectActionHandlers } from "@/object-actions/types";
import type { FileRecord, InboxItem } from "@cohesive/api-client";
import React from "react";
import type { BrowserItem } from "../home/utils/homeBrowserTypes";
import { getItemName } from "../types";
import { getDirectChildren } from "../utils/folderPaths";
import { FileBrowserFolderRow } from "./BrowserFolderRow";

/** Cycle cap — a cyclic parent chain stops
 *  recursing instead of blowing the stack. */
const MAX_TREE_DEPTH = 64;

/** Muted line under an expanded empty folder — the left rail's
 *  "No files in folder." treatment, indented to the children's depth. */
function TreeEmptyState({ label, depth }: { label: string; depth: number }) {
  return (
    <p
      className="px-2.5 py-2 text-sm text-n-40"
      style={depth > 0 ? { paddingLeft: `${10 + depth * 20}px` } : undefined}
    >
      {label}
    </p>
  );
}

export interface TreeExpansion {
  isOpen: (key: string) => boolean;
  onToggle: (key: string) => void;
}

/** Chats are a flat list — no folders (the tree organizes canvases and files). */
export function ChatBrowserTree({
  chats,
  sortItems,
  renderRow,
}: {
  chats: InboxItem[];
  /** Owning workspace — kept for call-site symmetry with FilesBrowserTree. */
  workspaceId: string;
  sortItems: (items: BrowserItem[]) => BrowserItem[];
  renderRow: (item: BrowserItem, depth: number) => React.ReactNode;
}) {
  const items = sortItems(
    chats.map((data): BrowserItem => ({ kind: "chat", data })),
  );
  if (items.length === 0) {
    return <TreeEmptyState label="No chats yet." depth={0} />;
  }
  return <>{items.map((item) => renderRow(item, 0))}</>;
}

/**
 * File flavor — path-based: folders are records whose path ends in `/`,
 * expansion keys on the folder path so it survives renames of siblings.
 */
export function FilesBrowserTree({
  records,
  parentPath = "",
  workspaceId,
  expansion,
  depth = 0,
  suppressedRecordIds,
  sortItems,
  renderRow,
  fileFolderDeps,
  folderChildCount,
  fileFolderExtraHandlers,
}: {
  records: FileRecord[];
  parentPath?: string;
  /** Owning workspace — folder rows are move drop targets scoped to it. */
  workspaceId: string;
  expansion: TreeExpansion;
  depth?: number;
  suppressedRecordIds?: Set<string>;
  sortItems: (items: BrowserItem[]) => BrowserItem[];
  renderRow: (item: BrowserItem, depth: number) => React.ReactNode;
  fileFolderDeps: FileRecordActionHandlerDeps;
  folderChildCount: (record: FileRecord) => number;
  fileFolderExtraHandlers?: (record: FileRecord) => ObjectActionHandlers;
}) {
  if (depth >= MAX_TREE_DEPTH) return null;
  const direct = getDirectChildren(records, parentPath).filter(
    (r) => r.status !== "pending" && r.status !== "deleted",
  );
  const folders = direct
    .filter((r) => r.path.endsWith("/"))
    .sort((a, b) => getItemName(a).localeCompare(getItemName(b)));
  const items = sortItems(
    direct
      .filter((r) => !r.path.endsWith("/") && !suppressedRecordIds?.has(r.id))
      .flatMap((record): BrowserItem[] =>
        record.kind === "canvas"
          ? record.canvas
            ? [{ kind: "canvas", data: record.canvas }]
            : []
          : [{ kind: "file", data: record }],
      ),
  );

  if (folders.length === 0 && items.length === 0) {
    return <TreeEmptyState label="No files in folder." depth={depth} />;
  }

  return (
    <>
      {folders.map((record) => (
        <React.Fragment key={record.id}>
          <FileBrowserFolderRow
            record={record}
            workspaceId={workspaceId}
            childCount={folderChildCount(record)}
            open={expansion.isOpen(record.path)}
            onToggle={() => expansion.onToggle(record.path)}
            depth={depth}
            handlerDeps={fileFolderDeps}
            extraHandlers={fileFolderExtraHandlers?.(record)}
          />
          {expansion.isOpen(record.path) && (
            <FilesBrowserTree
              records={records}
              parentPath={record.path}
              workspaceId={workspaceId}
              expansion={expansion}
              depth={depth + 1}
              suppressedRecordIds={suppressedRecordIds}
              sortItems={sortItems}
              renderRow={renderRow}
              fileFolderDeps={fileFolderDeps}
              fileFolderExtraHandlers={fileFolderExtraHandlers}
              folderChildCount={folderChildCount}
            />
          )}
        </React.Fragment>
      ))}
      {items.map((item) => renderRow(item, depth))}
    </>
  );
}
